import type { SortField, TableData, UseTableSortReturn } from '@/types';
import { useCallback, useMemo, useState } from 'react';
import { sortData } from '@/utils/tableUtils/tableSortUtils';

export const useTableSort = <T extends TableData>(
  tableData: T[],
  onSortCallback?: (sortField: SortField) => void,
  initialSortField?: SortField | null
): UseTableSortReturn<T> => {
  const [sortField, setSortField] = useState<SortField | null>(initialSortField ?? null);

  const handleSort = (field: string) => {
    setSortField((current) => {
      let newSortField: SortField | null = null;

      if (!current || current.key !== field) {
        newSortField = { key: field, direction: 'asc' };
      } else if (current.direction === 'asc') {
        newSortField = { key: field, direction: 'desc' };
      }

      if (newSortField && onSortCallback) {
        onSortCallback(newSortField);
      }

      return newSortField;
    });
  };

  const sortedData = useMemo(() => {
    return sortData(tableData, sortField);
  }, [tableData, sortField]);

  const applySortFromTable = useCallback(
    (next: SortField | null) => {
      setSortField(next);
      if (next !== null && onSortCallback) {
        onSortCallback(next);
      }
    },
    [onSortCallback]
  );

  return {
    sortField,
    sortedData,
    handleSort,
    setSortField,
    applySortFromTable,
  };
};
